import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  MenuItem, 
  CircularProgress,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  SelectChangeEvent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse,
  Fade,
  Avatar,
  Card,
  CardContent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { 
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Create as CreateIcon,
  Update as UpdateIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Send as SendIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import PageHeader from '../components/common/PageHeader';
import DataTable, { Column } from '../components/common/DataTable';
import { AuditService } from '../services/audit.service';
import { UserService } from '../services/user.service';
import { AuditLog as AuditLogType, AuditActionType, AuditLogQuery } from '../types/audit.types';
import { User } from '../types/auth.types';

export default function AuditLog() {
  const [logs, setLogs] = useState<AuditLogType[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  // Фильтры
  const [filters, setFilters] = useState<AuditLogQuery>({
    page: 1,
    pageSize: 20,
  });
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedActions, setSelectedActions] = useState<AuditActionType[]>([]);
  const [selectedEntityType, setSelectedEntityType] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Список возможных типов сущностей
  const entityTypes = [
    { value: 'user', label: 'Пользователь' },
    { value: 'table', label: 'Таблица' },
    { value: 'table_column', label: 'Колонка таблицы' },
    { value: 'table_data', label: 'Данные таблицы' },
    { value: 'table_permission', label: 'Разрешение таблицы' },
    { value: 'data_transfer', label: 'Передача данных' },
  ];
  
  // Список возможных действий
  const actionTypes = [
    { value: AuditActionType.CREATE, label: 'Создание', icon: <CreateIcon />, color: '#4caf50' },
    { value: AuditActionType.READ, label: 'Чтение', icon: <VisibilityIcon />, color: '#9c27b0' },
    { value: AuditActionType.UPDATE, label: 'Обновление', icon: <UpdateIcon />, color: '#2196f3' },
    { value: AuditActionType.DELETE, label: 'Удаление', icon: <DeleteIcon />, color: '#f44336' },
    { value: AuditActionType.LOGIN, label: 'Вход в систему', icon: <LoginIcon />, color: '#00bcd4' },
    { value: AuditActionType.LOGOUT, label: 'Выход из системы', icon: <LogoutIcon />, color: '#ff9800' },
    { value: AuditActionType.TRANSFER, label: 'Передача данных', icon: <SendIcon />, color: '#673ab7' },
  ];
  
  // Загрузка данных при монтировании компонента
  useEffect(() => {
    fetchUsers();
    fetchLogs();
  }, []);
  
  // Загрузка данных при изменении фильтров, страницы или количества строк
  useEffect(() => {
    fetchLogs();
  }, [filters, page, rowsPerPage]);
  
  // Загрузка списка пользователей
  const fetchUsers = async () => {
    try {
      const data = await UserService.getAllUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки пользователей');
    }
  };
  
  // Загрузка журнала аудита
  const fetchLogs = async () => {
    try {
      setLoading(true);
      
      // Формируем параметры запроса
      const query: AuditLogQuery = {
        ...filters,
        page: page + 1, // API использует 1-based pagination
        pageSize: rowsPerPage,
      };
      
      const response = await AuditService.getAuditLogs(query);
      setLogs(response.data);
      setTotalCount(response.total);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки журнала аудита');
    } finally {
      setLoading(false);
    }
  };
  
  // Обработчик изменения пользователя
  const handleUserChange = (event: SelectChangeEvent<string>) => {
    setSelectedUser(event.target.value);
  };
  
  // Обработчик изменения типа сущности
  const handleEntityTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedEntityType(event.target.value);
  };
  
  // Обработчик изменения действий
  const handleActionChange = (action: AuditActionType) => {
    setSelectedActions(prev => {
      if (prev.includes(action)) {
        return prev.filter(a => a !== action);
      } else {
        return [...prev, action];
      }
    });
  };
  
  // Применение фильтров
  const applyFilters = () => {
    const newFilters: AuditLogQuery = {
      page: 1,
      pageSize: rowsPerPage,
    };
    
    if (selectedUser) {
      newFilters.userId = selectedUser;
    }
    
    if (selectedEntityType) {
      newFilters.entityType = selectedEntityType;
    }
    
    if (selectedActions.length > 0) {
      newFilters.actions = selectedActions;
    }
    
    if (startDate) {
      newFilters.startDate = startDate.format('YYYY-MM-DD');
    }
    
    if (endDate) {
      newFilters.endDate = endDate.format('YYYY-MM-DD');
    }
    
    setFilters(newFilters);
    setPage(0); // Сбрасываем на первую страницу
  };
  
  // Сброс фильтров
  const clearFilters = () => {
    setSelectedUser('');
    setSelectedEntityType('');
    setSelectedActions([]);
    setStartDate(null);
    setEndDate(null);
    
    setFilters({
      page: 1,
      pageSize: rowsPerPage,
    });
    
    setPage(0); // Сбрасываем на первую страницу
  };
  
  // Обновление данных
  const handleRefresh = () => {
    fetchLogs();
  };
  
  // Обработчик изменения страницы
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  // Обработчик изменения количества строк на странице
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Сбрасываем на первую страницу
  };

  // Получение конфигурации действия
  const getActionConfig = (action: AuditActionType) => {
    return actionTypes.find(a => a.value === action) || {
      value: action,
      label: action,
      icon: <AssessmentIcon />,
      color: '#757575'
    };
  };
  
  // Колонки для таблицы
  const columns: Column[] = [
    {
      id: 'createdAt',
      label: 'Дата/время',
      format: (value) => dayjs(value).format('DD.MM.YYYY HH:mm:ss'),
    },
    {
      id: 'user',
      label: 'Пользователь',
      format: (value) => value?.fullName || '-',
    },
    {
      id: 'action',
      label: 'Действие',
      format: (value) => {
        const config = getActionConfig(value);
        return (
          <Chip
            icon={config.icon}
            label={config.label}
            size="small"
            sx={{
              bgcolor: `${config.color}15`,
              color: config.color,
              fontWeight: 600,
              '& .MuiChip-icon': {
                color: config.color,
              }
            }}
          />
        );
      },
    },
    {
      id: 'entityType',
      label: 'Тип объекта',
      format: (value) => getEntityTypeName(value),
    },
    {
      id: 'entityId',
      label: 'ID объекта',
      format: (value) => value ? (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {value.substring(0, 8)}...
        </Typography>
      ) : '-',
    },
    {
      id: 'details',
      label: 'Детали',
      format: (value) => formatDetails(value),
    },
    {
      id: 'ipAddress',
      label: 'IP-адрес',
      format: (value) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {value || '-'}
        </Typography>
      ),
    },
  ];

  // Проверка активности фильтров
  const hasActiveFilters = selectedUser || selectedEntityType || selectedActions.length > 0 || startDate || endDate;
  
  return (
    <Box>
      <PageHeader 
        title="Журнал аудита"
        subtitle="История действий пользователей в системе"
      />
      
      {error && (
        <Fade in>
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Статистика */}
      <Fade in timeout={600}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {totalCount}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Всего записей
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <AssessmentIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>
      
      {/* Фильтры */}
      <Paper 
        sx={{ 
          mb: 3,
          borderRadius: 3,
          overflow: 'hidden',
          transition: 'all 0.3s',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: showFilters 
              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
              : 'white',
            borderBottom: showFilters ? '1px solid rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.3s',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                Фильтры
              </Typography>
              {hasActiveFilters && (
                <Chip 
                  label="Активны" 
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Обновить">
                <IconButton 
                  onClick={handleRefresh}
                  sx={{
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'rotate(180deg)',
                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                    }
                  }}
                >
                  <RefreshIcon sx={{ color: '#667eea' }} />
                </IconButton>
              </Tooltip>
              
              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterIcon />}
                endIcon={
                  <ExpandMoreIcon 
                    sx={{
                      transition: 'transform 0.3s',
                      transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                }
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  ...(showFilters ? {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    }
                  } : {
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': {
                      borderColor: '#667eea',
                      bgcolor: 'rgba(102, 126, 234, 0.08)',
                    }
                  })
                }}
              >
                {showFilters ? 'Скрыть' : 'Показать'}
              </Button>
            </Box>
          </Box>
        </Box>
        
        <Collapse in={showFilters}>
          <Box sx={{ p: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{xs:12, sm:6, md:3}}>
                  <FormControl fullWidth>
                    <InputLabel id="user-select-label">Пользователь</InputLabel>
                    <Select
                      labelId="user-select-label"
                      value={selectedUser}
                      label="Пользователь"
                      onChange={handleUserChange}
                      sx={{
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                        }
                      }}
                    >
                      <MenuItem value="">Все пользователи</MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.fullName} ({user.username})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{xs:12, sm:6, md:3}}>
                  <FormControl fullWidth>
                    <InputLabel id="entity-type-select-label">Тип объекта</InputLabel>
                    <Select
                      labelId="entity-type-select-label"
                      value={selectedEntityType}
                      label="Тип объекта"
                      onChange={handleEntityTypeChange}
                      sx={{
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                        }
                      }}
                    >
                      <MenuItem value="">Все типы</MenuItem>
                      {entityTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{xs:12, sm:6, md:3}}>
                  <DatePicker
                    label="Начальная дата"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }
                      },
                    }}
                  />
                </Grid>
                
                <Grid size={{xs:12, sm:6, md:3}}>
                  <DatePicker
                    label="Конечная дата"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }
                      },
                    }}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#667eea' }}>
                  Действия:
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {actionTypes.map((action) => (
                    <Chip
                      key={action.value}
                      icon={action.icon}
                      label={action.label}
                      onClick={() => handleActionChange(action.value)}
                      variant={selectedActions.includes(action.value) ? 'filled' : 'outlined'}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        transition: 'all 0.2s',
                        ...(selectedActions.includes(action.value) ? {
                          bgcolor: `${action.color}15`,
                          color: action.color,
                          borderColor: action.color,
                          '& .MuiChip-icon': {
                            color: action.color,
                          },
                          '&:hover': {
                            bgcolor: `${action.color}25`,
                          }
                        } : {
                          borderColor: action.color,
                          color: action.color,
                          '& .MuiChip-icon': {
                            color: action.color,
                          },
                          '&:hover': {
                            bgcolor: `${action.color}10`,
                          }
                        })
                      }}
                    />
                  ))}
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#f44336',
                    color: '#f44336',
                    '&:hover': {
                      borderColor: '#f44336',
                      bgcolor: 'rgba(244, 67, 54, 0.08)',
                    }
                  }}
                >
                  Сбросить
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={applyFilters}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                    }
                  }}
                >
                  Применить
                </Button>
              </Box>
            </LocalizationProvider>
          </Box>
        </Collapse>
      </Paper>

      {/* Таблица */}
      <DataTable
        columns={columns}
        data={logs}
        loading={loading}
        emptyText="Записи журнала не найдены"
        pagination={{
          count: totalCount,
          page,
          rowsPerPage,
          onPageChange: handlePageChange,
          onRowsPerPageChange: handleRowsPerPageChange,
        }}
      />
    </Box>
  );
}

// Вспомогательные функции для форматирования данных

// Получение названия типа сущности
function getEntityTypeName(entityType?: string): string {
  if (!entityType) return '-';
  
  const names: Record<string, string> = {
    'user': 'Пользователь',
    'table': 'Таблица',
    'table_column': 'Колонка таблицы',
    'table_data': 'Данные таблицы',
    'table_permission': 'Разрешение таблицы',
    'data_transfer': 'Передача данных',
  };
  
  return names[entityType] || entityType;
}

// Форматирование деталей
function formatDetails(details?: Record<string, any>): React.ReactNode {
  if (!details) return '-';
  
  const detailsArray = Object.entries(details).map(([key, value]) => {
    const displayValue = typeof value === 'object' 
      ? Array.isArray(value) 
        ? value.join(', ') 
        : JSON.stringify(value)
      : String(value);
    
    return `${key}: ${displayValue}`;
  });
  
  return (
    <Tooltip title={detailsArray.join(' • ')} placement="top">
      <Typography 
        variant="body2" 
        sx={{ 
          maxWidth: 250, 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: 'help',
        }}
      >
        {detailsArray.slice(0, 2).join(' • ')}
        {detailsArray.length > 2 && ' ...'}
      </Typography>
    </Tooltip>
  );
}